import * as React from "react";
import { cn } from "@/lib/utils";

/* =====================================================================
 * LOWKEYX // OPERATOR CONSOLE — proprietary primitives
 * Bespoke components, not derived from shadcn defaults.
 * ===================================================================== */

/* ------------------------------ ModuleCard --------------------------- */

type Status = "ok" | "warn" | "err" | "info" | "off";

const dotByStatus: Record<Status, string> = {
  ok: "dot-on",
  warn: "dot-warn",
  err: "dot-err",
  info: "dot-info",
  off: "bg-muted",
};

export interface ModuleCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Mono ID shown in the title bar, e.g. "MOD_03 · OSINT" */
  moduleId?: string;
  /** Right-side meta in title bar */
  meta?: React.ReactNode;
  /** Status dot in title bar */
  status?: Status;
  /** Use diagonal corner clip */
  clip?: boolean;
  /** Hide the title bar entirely */
  bare?: boolean;
}

export const ModuleCard = React.forwardRef<HTMLDivElement, ModuleCardProps>(
  ({ className, moduleId, meta, status = "off", clip = false, bare = false, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "module-surface relative",
        clip && "clip-corner-sm",
        className,
      )}
      {...props}
    >
      {!bare && (
        <div className="module-titlebar">
          <div className="flex items-center gap-2 min-w-0">
            <span className={cn("dot", dotByStatus[status], status !== "off" && "animate-pulse-signal")} />
            <span className="truncate">{moduleId ?? "MODULE"}</span>
          </div>
          {meta && <div className="flex items-center gap-2 text-foreground/70">{meta}</div>}
        </div>
      )}
      <div className={cn(!bare && "p-5")}>{children}</div>
    </div>
  ),
);
ModuleCard.displayName = "ModuleCard";

/* ------------------------------ HexBadge ----------------------------- */

export const HexBadge: React.FC<React.HTMLAttributes<HTMLSpanElement>> = ({
  className,
  children,
  ...props
}) => (
  <span
    className={cn(
      "inline-flex items-center justify-center clip-hex bg-primary/15 border border-primary/40 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-primary min-w-[64px]",
      className,
    )}
    {...props}
  >
    {children}
  </span>
);

/* ------------------------------ StatChip ----------------------------- */

export interface StatChipProps {
  label: string;
  value: React.ReactNode;
  delta?: string;
  trend?: "up" | "down" | "flat";
  className?: string;
}

export const StatChip: React.FC<StatChipProps> = ({ label, value, delta, trend = "flat", className }) => {
  const trendColor =
    trend === "up" ? "text-signal-green" : trend === "down" ? "text-signal-red" : "text-muted-foreground";
  const arrow = trend === "up" ? "▲" : trend === "down" ? "▼" : "■";
  return (
    <div className={cn("module-surface px-4 py-3 flex flex-col gap-1", className)}>
      <span className="label-mono">{label}</span>
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-display text-2xl font-semibold tabular-nums">{value}</span>
        {delta && (
          <span className={cn("font-mono text-[11px]", trendColor)}>
            {arrow} {delta}
          </span>
        )}
      </div>
    </div>
  );
};

/* ----------------------------- SectionHeader ------------------------- */

export interface SectionHeaderProps {
  /** Numbered signature, e.g. "03" */
  index?: string;
  /** Eyebrow, e.g. "CAPABILITIES" */
  eyebrow: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  align?: "left" | "center";
  action?: React.ReactNode;
  className?: string;
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({
  index,
  eyebrow,
  title,
  description,
  align = "left",
  action,
  className,
}) => (
  <header
    className={cn(
      "flex flex-col gap-3 md:flex-row md:items-end md:justify-between border-b border-border pb-4",
      align === "center" && "md:items-center text-center",
      className,
    )}
  >
    <div className={cn("max-w-2xl", align === "center" && "mx-auto")}>
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">
        <span className="text-primary">//</span>{" "}
        {index && <span className="text-foreground/70 mr-2">{index.padStart(2, "0")}</span>}
        <span>{eyebrow}</span>
      </div>
      <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight leading-tight">
        {title}
      </h2>
      {description && (
        <p className="mt-3 text-sm md:text-base text-muted-foreground max-w-xl leading-relaxed">
          {description}
        </p>
      )}
    </div>
    {action && <div className="shrink-0">{action}</div>}
  </header>
);

/* --------------------------------- KPI ------------------------------- */

export interface KPIProps {
  label: string;
  value: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
  className?: string;
}

export const KPI: React.FC<KPIProps> = ({ label, value, prefix = "", suffix = "", duration = 1200, className }) => {
  const [n, setN] = React.useState(0);
  React.useEffect(() => {
    let raf = 0;
    const start = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(value * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <span className="label-mono">{label}</span>
      <div className="font-display text-3xl md:text-4xl font-semibold tabular-nums leading-none">
        {prefix}
        {n.toLocaleString()}
        {suffix}
      </div>
    </div>
  );
};

/* ----------------------------- CommandHint --------------------------- */

export const CommandHint: React.FC<{ keys: string[]; className?: string }> = ({ keys, className }) => (
  <span className={cn("inline-flex items-center gap-1", className)}>
    {keys.map((k, i) => (
      <kbd
        key={i}
        className="inline-flex items-center justify-center min-w-[20px] h-5 px-1 border border-border bg-surface-2 font-mono text-[10px] text-muted-foreground"
      >
        {k}
      </kbd>
    ))}
  </span>
);

/* ------------------------------ TitleBar ----------------------------- */
/* Standalone title bar (use inside custom containers) */

export const TitleBar: React.FC<React.HTMLAttributes<HTMLDivElement> & {
  status?: Status;
  meta?: React.ReactNode;
}> = ({ children, status = "off", meta, className, ...props }) => (
  <div className={cn("module-titlebar", className)} {...props}>
    <div className="flex items-center gap-2 min-w-0">
      <span className={cn("dot", dotByStatus[status], status !== "off" && "animate-pulse-signal")} />
      <span className="truncate">{children}</span>
    </div>
    {meta && <div className="flex items-center gap-2 text-foreground/70">{meta}</div>}
  </div>
);

/* ------------------------------ MetaChip ----------------------------- */
/* Compact mono chip for displaying metadata pairs (icon + label).      */

export interface MetaChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  icon?: React.ReactNode;
  tone?: "default" | "primary" | "info" | "warn" | "ok";
}

const toneClasses: Record<NonNullable<MetaChipProps["tone"]>, string> = {
  default: "border-border text-muted-foreground",
  primary: "border-primary/40 text-primary bg-primary/5",
  info: "border-info/40 text-info bg-info/5",
  warn: "border-warning/40 text-warning bg-warning/5",
  ok: "border-terminal/40 text-terminal bg-terminal/5",
};

export const MetaChip: React.FC<MetaChipProps> = ({
  icon,
  tone = "default",
  className,
  children,
  ...props
}) => (
  <span
    className={cn(
      "inline-flex items-center gap-1.5 border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] leading-none whitespace-nowrap",
      toneClasses[tone],
      className,
    )}
    {...props}
  >
    {icon && <span className="inline-flex items-center [&_svg]:w-2.5 [&_svg]:h-2.5">{icon}</span>}
    <span className="tabular-nums">{children}</span>
  </span>
);

/* ------------------------------ DossierCard -------------------------- */
/* Editorial card for articles, fundamentals, stories. Rectangular,    */
/* hairline border, mono code in upper-left, hover sweep.               */

export interface DossierCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Mono identifier shown in the corner, e.g. "ART_023" */
  code?: string;
  /** Eyebrow text (category, section) */
  eyebrow?: React.ReactNode;
  /** Right side of header row (badge, status) */
  badge?: React.ReactNode;
  /** Accent color token name, e.g. "primary", "info", "warning" */
  accent?: "primary" | "info" | "warning" | "terminal" | "muted";
  /** Show diagonal corner clip */
  clip?: boolean;
  /** Featured image URL (renders banner above content) */
  image?: string;
  imageAlt?: string;
}

const accentMap: Record<NonNullable<DossierCardProps["accent"]>, string> = {
  primary: "before:bg-primary",
  info: "before:bg-info",
  warning: "before:bg-warning",
  terminal: "before:bg-terminal",
  muted: "before:bg-muted-foreground/40",
};

export const DossierCard = React.forwardRef<HTMLDivElement, DossierCardProps>(
  ({ className, code, eyebrow, badge, accent = "primary", clip = false, image, imageAlt, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "group relative bg-card border border-border text-card-foreground transition-colors duration-200",
        "hover:border-border-strong",
        // Left accent rail
        "before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[2px] before:opacity-60 before:transition-opacity group-hover:before:opacity-100",
        accentMap[accent],
        clip && "clip-corner-sm",
        className,
      )}
      {...props}
    >
      {/* Top hairline header */}
      {(code || eyebrow || badge) && (
        <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-border/60">
          <div className="flex items-center gap-2 min-w-0">
            {code && (
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                {code}
              </span>
            )}
            {code && eyebrow && <span className="text-muted-foreground/40">·</span>}
            {eyebrow && (
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-foreground/70 truncate">
                {eyebrow}
              </span>
            )}
          </div>
          {badge && <div className="shrink-0">{badge}</div>}
        </div>
      )}

      {image && (
        <div className="relative w-full aspect-[16/9] overflow-hidden bg-surface-2">
          <img
            src={image}
            alt={imageAlt ?? ""}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-card via-card/20 to-transparent opacity-80" />
        </div>
      )}

      <div className="p-4">{children}</div>

      {/* Hover sweep */}
      <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-foreground/[0.03] to-transparent skew-x-[-12deg] group-hover:translate-x-[400%] transition-transform duration-1000 ease-out" />
      </div>
    </div>
  ),
);
DossierCard.displayName = "DossierCard";

/* ------------------------------ FilterRail --------------------------- */
/* Compact tag/filter strip with mono labels.                           */

export interface FilterRailItem {
  id: string;
  label: React.ReactNode;
  count?: number;
}

export interface FilterRailProps {
  items: FilterRailItem[];
  activeId?: string;
  onSelect?: (id: string) => void;
  className?: string;
}

export const FilterRail: React.FC<FilterRailProps> = ({ items, activeId, onSelect, className }) => (
  <div className={cn("flex flex-wrap gap-1.5", className)}>
    {items.map((item) => {
      const active = item.id === activeId;
      return (
        <button
          key={item.id}
          type="button"
          onClick={() => onSelect?.(item.id)}
          className={cn(
            "inline-flex items-center gap-1.5 border px-2.5 py-1 font-mono text-[11px] uppercase tracking-[0.12em] transition-colors",
            active
              ? "bg-primary text-primary-foreground border-primary"
              : "border-border text-muted-foreground hover:text-foreground hover:border-border-strong",
          )}
        >
          <span>{item.label}</span>
          {typeof item.count === "number" && (
            <span className={cn("tabular-nums opacity-70", active && "opacity-100")}>
              [{item.count}]
            </span>
          )}
        </button>
      );
    })}
  </div>
);